import { Box, Grid, Heading, Link, Text } from '@chakra-ui/react'
import { Section } from '@/components/layout'
import { SectionLabel } from '@/components/typography'
import { useLocale } from '@/i18n'
import { padNumber } from '@/lib/padNumber'
import {
  FAILED_STARTUPS,
  SELECTED_WORK,
  SELECTED_WORK_FOOTNOTE,
  SELECTED_WORK_LABEL,
} from './caseStudies.data'

interface WorkRowProps {
  number?: string
  client: string
  line: string
  url?: string
}

const WorkRow = ({ number, client, line, url }: WorkRowProps) => (
  <Grid
    borderBottom="2px solid"
    borderColor="divider"
    py="22px"
    templateColumns={{ base: '40px minmax(0, 1fr)', lg: '56px 260px minmax(0, 1fr)' }}
    gap="clamp(4px, 1.6vw, 24px)"
    alignItems="baseline"
  >
    <Text
      fontFamily="heading"
      fontWeight="800"
      fontSize="13px"
      letterSpacing="0.12em"
      color="accent"
      display={{ base: number ? 'block' : 'none', lg: 'block' }}
    >
      {number}
    </Text>
    <Heading
      as="h4"
      fontSize="22px"
      fontWeight="800"
      letterSpacing="-0.02em"
      gridColumn={{ base: 2, lg: 'auto' }}
    >
      {url ? (
        <Link
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          color="accent"
          textDecoration="none"
          _hover={{ color: 'accent.600' }}
        >
          {client}
        </Link>
      ) : (
        client
      )}
    </Heading>
    <Text
      fontSize="15px"
      lineHeight="1.62"
      color="muted"
      gridColumn={{ base: 2, lg: 'auto' }}
      mt={{ base: '6px', lg: 0 }}
    >
      {line}
    </Text>
  </Grid>
)

export const CaseStudies = () => {
  const { locale } = useLocale()

  return (
    <Section id="work">
      <SectionLabel mb="32px">{SELECTED_WORK_LABEL[locale]}</SectionLabel>
      <Box borderTop="2px solid" borderColor="divider">
        {SELECTED_WORK[locale].map((caseStudy, index) => (
          <WorkRow key={caseStudy.client} number={padNumber(index)} {...caseStudy} />
        ))}
      </Box>
      {locale === 'en' && (
        <Box mt="48px">
          <SectionLabel mb="4px">Failed startups we contributed to</SectionLabel>
          <Text mb="20px" fontSize="13px" fontStyle="italic" color="muted">
            Collector&apos;s favorites. Every long career ships a few.
          </Text>
          <Box borderTop="2px solid" borderColor="divider">
            {FAILED_STARTUPS.map((caseStudy) => (
              <WorkRow key={caseStudy.client} {...caseStudy} />
            ))}
          </Box>
        </Box>
      )}
      <Text mt="32px" fontSize="15px" lineHeight="1.62" maxW="88ch" color="muted">
        {SELECTED_WORK_FOOTNOTE[locale]}
      </Text>
    </Section>
  )
}
